// ======================================================================
// ImsPage.aggregation.js
// Pure aggregation functions for IMS Reports
//
// Tutte queste funzioni sono pure: non usano state/hooks React.
// Possono essere chiamate liberamente da qualsiasi contesto.
// ======================================================================

// ======================================================================
// AGGREGAZIONE GLOBALE: somma tutte le regioni e province per SKU
// ======================================================================
export const aggregateGlobally = (rows) => {
  const grouped = {};
  const provsSet = [...new Set(rows.map((r) => r.prov || ""))].filter(Boolean);
  const regionsSet = [...new Set(rows.map((r) => r.regione || ""))].filter(Boolean);
  const _provs = provsSet.join(", ");
  const _regions = regionsSet.join(", ");

  rows.forEach((row) => {
    const key = row.sku;
    if (!grouped[key]) {
      grouped[key] = {
        ...row,
        regione: _regions,
        prov: _provs,
        periodi: {},
        fatturato: {},
      };
    }
    Object.keys(row.periodi || {}).forEach((periodo) => {
      if (!grouped[key].periodi[periodo]) grouped[key].periodi[periodo] = 0;
      if (!grouped[key].fatturato[periodo]) grouped[key].fatturato[periodo] = 0;
      grouped[key].periodi[periodo] += row.periodi[periodo] || 0;
      grouped[key].fatturato[periodo] += row.fatturato[periodo] || 0;
    });
  });

  return Object.values(grouped);
};

// ======================================================================
// AGGREGAZIONE PER PROVINCIA: somma province per prodotto×regione
// ======================================================================
export const aggregateByProvince = (rows) => {
  const grouped = {};
  const provsSet = [...new Set(rows.map((r) => r.prov || ""))].filter(Boolean);
  const _provs = provsSet.join(", ");

  rows.forEach((row) => {
    const key = `${row.sku}-${row.regione}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...row,
        prov: _provs,
        periodi: {},
        fatturato: {},
      };
    }
    Object.keys(row.periodi || {}).forEach((periodo) => {
      if (!grouped[key].periodi[periodo]) grouped[key].periodi[periodo] = 0;
      if (!grouped[key].fatturato[periodo]) grouped[key].fatturato[periodo] = 0;
      grouped[key].periodi[periodo] += row.periodi[periodo] || 0;
      grouped[key].fatturato[periodo] += row.fatturato[periodo] || 0;
    });
  });

  return Object.values(grouped);
};

// ======================================================================
// AGGREGAZIONE PER REGIONE: raggruppa per prodotto×provincia
// ======================================================================
export const aggregateByRegion = (rows) => {
  const grouped = {};

  rows.forEach((row) => {
    const key = `${row.sku}-${row.prov}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...row,
        regione: row.regione,
        periodi: {},
        fatturato: {},
      };
    }
    Object.keys(row.periodi || {}).forEach((periodo) => {
      if (!grouped[key].periodi[periodo]) grouped[key].periodi[periodo] = 0;
      if (!grouped[key].fatturato[periodo]) grouped[key].fatturato[periodo] = 0;
      grouped[key].periodi[periodo] += row.periodi[periodo] || 0;
      grouped[key].fatturato[periodo] += row.fatturato[periodo] || 0;
    });
  });

  return Object.values(grouped);
};

// ======================================================================
// AGGREGAZIONE PER LINEA: raggruppa per SKU×line×regione
// ======================================================================
export const aggregateByLine = (rows) => {
  const grouped = {};
  const sorted = [...rows].sort((a, b) => (a.linea || "").localeCompare(b.linea || ""));
  const provsSet = [...new Set(rows.map((r) => r.prov || ""))].filter(Boolean);
  const _provs = provsSet.join(", ");

  sorted.forEach((row) => {
    const key = `${row.sku}-${row.line}-${row.regione}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...row,
        regione: row.regione,
        prov: _provs,
        periodi: {},
        fatturato: {},
      };
    }
    Object.keys(row.periodi || {}).forEach((periodo) => {
      if (!grouped[key].periodi[periodo]) grouped[key].periodi[periodo] = 0;
      if (!grouped[key].fatturato[periodo]) grouped[key].fatturato[periodo] = 0;
      grouped[key].periodi[periodo] += row.periodi[periodo] || 0;
      grouped[key].fatturato[periodo] += row.fatturato[periodo] || 0;
    });
  });

  return Object.values(grouped);
};

// ======================================================================
// RAGGRUPPA DATI PER REGIONE (per visualizzazione separata)
// ======================================================================
export const groupDataByRegion = (rows) => {
  const grouped = {};
  rows.forEach((row) => {
    const regionKey = row.regione || "Senza Regione";
    if (!grouped[regionKey]) grouped[regionKey] = [];
    grouped[regionKey].push(row);
  });
  return grouped;
};

// ======================================================================
// RAGGRUPPA DATI PER PROVINCIA (per visualizzazione separata)
// ======================================================================
export const groupDataByProvince = (rows) => {
  const grouped = {};
  rows.forEach((row) => {
    const provinceKey = row.prov || "Senza Provincia";
    if (!grouped[provinceKey]) grouped[provinceKey] = [];
    grouped[provinceKey].push(row);
  });
  return grouped;
};

// ======================================================================
// RAGGRUPPA DATI PER LINEA (per visualizzazione separata)
// ======================================================================
export const groupDataByLine = (rows) => {
  const grouped = {};
  rows.forEach((row) => {
    const lineKey = row.linea || "Senza Linea";
    if (!grouped[lineKey]) grouped[lineKey] = [];
    grouped[lineKey].push(row);
  });
  return grouped;
};
